import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarDays, MapPin, Plus, Users, Clock } from "lucide-react";
import MessageButton from "@/components/MessageButton";
import CommunityFilterBar from "@/components/CommunityFilterBar";
import { useCommunityFilter } from "@/hooks/useCommunityFilter";
import { format } from "date-fns";

const CATEGORIES = ["meetup", "meeting", "sports", "market", "culture"];

function fmtDate(iso) {
  try { return format(new Date(iso), "EEE, MMM d · h:mm a"); }
  catch { return iso; }
}

export default function Events() {
  const { user } = useAuth();
  const community = useCommunityFilter();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("18:00");
  const [form, setForm] = useState({
    title: "", description: "", location: "", category: "meetup",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = community.filterParam ? { community: community.filterParam } : {};
      const { data } = await api.get("/events", { params });
      setEvents(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = "Events & Meetings · Jirani"; }, []);
  useEffect(() => {
    if (community.loading) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community.filterParam, community.loading]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const [h, m] = time.split(":").map((v) => parseInt(v, 10) || 0);
      const d = new Date(date);
      d.setHours(h, m, 0, 0);
      const payload = { ...form, starts_at: d.toISOString() };
      const { data } = await api.post("/events", payload);
      setEvents([data, ...events]);
      setOpen(false);
      setForm({ title: "", description: "", location: "", category: "meetup" });
      setTime("18:00");
      toast.success("Event created — spread the word!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const rsvp = async (ev) => {
    if (!user) return toast("Please log in to RSVP.");
    try {
      const { data } = await api.post(`/events/${ev.id}/rsvp`);
      setEvents((es) => es.map((e) => e.id === ev.id ? {
        ...e,
        attendee_count: data.attendee_count,
        attendees: data.going
          ? Array.from(new Set([...(e.attendees || []), user.user_id]))
          : (e.attendees || []).filter((x) => x !== user.user_id),
      } : e));
      toast.success(data.going ? "You're in — karibu!" : "RSVP removed.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="events-page" className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="jirani-eyebrow">See you there</div>
          <h1 className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
            <CalendarDays className="h-8 w-8 text-[#D95D39]" /> Events & Meetings
          </h1>
          <p className="mt-2 max-w-xl text-[#4A4A4A]">
            Clean-ups, watch meetings, football fun days and more.
          </p>
          <div className="mt-4">
            <CommunityFilterBar
              active={community.active}
              showAll={community.showAll}
              onToggle={community.setShowAll}
            />
          </div>
        </div>

        {user && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-event-btn"
                className="jirani-pill rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
              >
                <Plus className="mr-2 h-4 w-4" /> Create event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Outfit" }}>Create an event</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="event-title-input" required maxLength={140}
                    value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 h-11 rounded-xl" />
                </div>
                <div>
                  <Label>Category</Label>
                  <select data-testid="event-category-select"
                    value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3">
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input data-testid="event-location-input" required
                    value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="mt-1 h-11 rounded-xl" placeholder="Park, hall, estate name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          data-testid="event-date-btn"
                          variant="outline"
                          className="mt-1 h-11 w-full justify-start rounded-xl border-black/10 bg-white text-left font-normal"
                        >
                          <CalendarDays className="mr-2 h-4 w-4 text-[#2E473B]" />
                          {format(date, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => d && setDate(d)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input data-testid="event-time-input" type="time" required
                      value={time} onChange={(e) => setTime(e.target.value)}
                      className="mt-1 h-11 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea data-testid="event-description-input" required rows={4}
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
                <DialogFooter>
                  <Button data-testid="event-submit-btn" type="submit" disabled={saving}
                    className="jirani-pill w-full rounded-full bg-[#D95D39] text-white hover:bg-[#c25333]">
                    {saving ? "Creating..." : "Publish event"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </header>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-2xl border border-black/5 bg-white/60" />
          ))}
        </div>
      ) : (
        <div data-testid="events-list" className="grid gap-5 md:grid-cols-2">
          {events.map((ev) => {
            const going = user && (ev.attendees || []).includes(user.user_id);
            return (
              <article
                key={ev.id}
                data-testid={`event-${ev.id}`}
                className="jirani-card relative flex flex-col rounded-2xl border border-black/5 bg-white p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <Badge className="rounded-full bg-[#F4A261]/20 text-[#2E473B] hover:bg-[#F4A261]/20">
                      {ev.category}
                    </Badge>
                    <h3 className="mt-3 text-lg text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
                      {ev.title}
                    </h3>
                    <div className="mt-1 flex items-start gap-1.5 text-sm text-[#2E473B]">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D95D39]" />
                      <div>
                        <div className="font-medium">{ev.location}</div>
                        {ev.community && (
                          <div className="text-[11px] uppercase tracking-widest text-[#2E473B]/70">
                            {ev.community} community
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 rounded-2xl border border-black/5 bg-[#F9F7F4] p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-[#2E473B]/70">
                      {new Date(ev.starts_at).toLocaleString("en", { month: "short" })}
                    </div>
                    <div className="text-2xl font-bold text-[#D95D39]" style={{ fontFamily: "Outfit" }}>
                      {new Date(ev.starts_at).getDate()}
                    </div>
                  </div>
                </div>

                <p className="mt-3 text-sm leading-relaxed text-[#4A4A4A]">{ev.description}</p>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#2E473B]/85">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDate(ev.starts_at)}</span>
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ev.attendee_count || 0} going</span>
                </div>

                <div className="mt-4">
                  <Button
                    data-testid={`rsvp-btn-${ev.id}`}
                    onClick={() => rsvp(ev)}
                    className={`jirani-pill rounded-full ${
                      going ? "bg-[#2E473B] text-white hover:bg-[#243830]" : "bg-[#D95D39] text-white hover:bg-[#c25333]"
                    }`}
                  >
                    {going ? "You're going ✓" : "RSVP"}
                  </Button>
                  {ev.author?.user_id && (
                    <span className="ml-2 inline-block align-middle">
                      <MessageButton peerId={ev.author.user_id} peerName={ev.author?.name} />
                    </span>
                  )}
                </div>
              </article>
            );
          })}
          {events.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]">
              No events yet — plan one!
            </div>
          )}
        </div>
      )}
    </div>
  );
}
